// DSS Documentation on SocoHttpService - http://tiny.sc/cgsocohttpservice
import {Injectable} from '@angular/core';
import {
  HttpClient,
  HttpHeaders,
  HttpRequest,
  HttpParams,
  HttpEventType,
  HttpResponse,
  HttpErrorResponse
} from '@angular/common/http';

import {v4} from 'uuid';
import {Subject, Observable, throwError} from 'rxjs';
import {map, filter, catchError, concatMap} from 'rxjs/operators';

// DSS Documentation on ApiResult - http://tiny.sc/cgapiresult
import {ApiResult} from '@app/soco/core';
// DSS Documentation on SocoConfigurationService - http://tiny.sc/cgsococonfigurationservice
import {SocoConfigurationService} from '@app/soco/configuration';
// DSS Documentation on SocoAlertService - http://tiny.sc/cgsocoalertservice
import {SocoAlertService} from '@app/soco/alert';
// DSS Documentation on SocoWebAuthService - http://tiny.sc/socowebauthservice
import {SocoWebAuthService} from '@app/soco/http/soco-web-auth.service';
import {ConstantsService} from '@app/core/services/constants.service';

// declare var swal: any;
/* istanbul ignore next */
@Injectable({
  providedIn: 'root'
})
export class SocoHttpService {
  private pleaseWaitShowSource = new Subject<any>();
  private pleaseWaitHideSource = new Subject<any>();
  pleaseWaitShown$ = this.pleaseWaitShowSource.asObservable();
  pleaseWaitHidden$ = this.pleaseWaitHideSource.asObservable();
  public sessionActive = true;

  // DSS Documentation on SocoConfigurationService - http://tiny.sc/cgsococonfigurationservice
  // DSS Documentation on SocoAlertService - http://tiny.sc/cgsocoalertservice
  // DSS Documentation on SocoWebAuthService - http://tiny.sc/socowebauthservice
  constructor(
      private httpClient: HttpClient,
      private config: SocoConfigurationService,
      private socoAlertService: SocoAlertService,
      private socoWebAuthService: SocoWebAuthService,
      private constantsSvc: ConstantsService
  ) {
  }

  /**
   *  Can be used within the error function called when subscribing to an
   *  observable to determine what type of exception occurred.
   *  @param result The result returned by SocoHttpService
   */
  getExceptionType(result: any): SocoHttpServiceExceptionType {
    let failureType = SocoHttpServiceExceptionType.Error;
    if (result != null) {
      if (result.status) {
        failureType = SocoHttpServiceExceptionType.None;
      } else if (result.modelErrors) {
        failureType = SocoHttpServiceExceptionType.Invalid;
      } else if (result.message) {
        failureType = SocoHttpServiceExceptionType.Failure;
      }
    }
    return failureType;
  }

  /**
   *  Can be used within the error function called when subscribing to an
   *  observable to determine if the result was an error. This would mean
   *  the result is not an ApiResult indicating a failure but rather some
   *  other type of exception.
   *  @param result The result returned by SocoHttpService
   */
  isErrorResult(result: any) {
    return this.getExceptionType(result) === SocoHttpServiceExceptionType.Error;
  }

  /**
   *  Can be used within the error function called when subscribing to an
   *  observable to determine if the result was a failure. This would mean
   *  something happened server-side so an ApiResult has been returned to
   *  provide more information on what happened.
   *  @param result The result returned by SocoHttpService
   */
  isFailureResult(result: any) {
    return (
        this.getExceptionType(result) === SocoHttpServiceExceptionType.Failure
    );
  }

  /**
   *  Can be used within the error function called when subscribing to an
   *  observable to determine if the result was invalid. This would mean
   *  the information in the model sent to the server had invalid data.
   *  @param result The result returned by SocoHttpService
   */
  isInvalidResult(result: any) {
    return (
        this.getExceptionType(result) === SocoHttpServiceExceptionType.Invalid
    );
  }

  /**
   *  Does an HTTP GET
   *  @param options The options to be used in the HTTP GET
   */
  get<T>(options: SocoHttpServiceOptions): Observable<ApiResult<T>> {
    options.verb = 'get';
    return this.webMethod<T>(options);
  }

  /**
   *  Does an HTTP POST
   *  @param options The options to be used in the HTTP POST
   */
  post<T>(options: SocoHttpServiceOptions): Observable<ApiResult<T>> {
    options.verb = 'post';
    return this.webMethod<T>(options);
  }

  /**
   *  Does an HTTP PUT
   *  @param options The options to be used in the HTTP PUT
   */
  put<T>(options: SocoHttpServiceOptions): Observable<ApiResult<T>> {
    options.verb = 'put';
    return this.webMethod<T>(options);
  }

  /**
   *  Does an HTTP DELETE
   *  @param options The options to be used in the HTTP DELETE
   */
  del<T>(options: SocoHttpServiceOptions): Observable<ApiResult<T>> {
    options.verb = 'delete';
    return this.webMethod<T>(options);
  }

  private webMethod<T>(
      options: SocoHttpServiceOptions
  ): Observable<ApiResult<T>> {
    // add the token and transaction id
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      Authorization: 'Bearer ' + this.config.webApiToken,
      'x-transaction-id': this.getGuid(),
      'Cache-Control': 'no-cache',
      Pragma: 'no-cache'
    });

    // add the WebApiBaseUrl to the front of every URL unless we were told not to
    if (!options.excludeWebApiBaseUrl) {
      options.url = this.config.webApiBaseUrl + options.url;
    }

    // include the params
    // let params = new HttpParams();
    // if (options.params) {
    //   for (const param in options.params) {
    //     if (options.params.hasOwnProperty(param)) {
    //       params = params.set(param, options.params[param]);
    //     }
    //   }
    // }
    // include the params
    let params = new HttpParams();
    for (const param in options.params) {
      const paramName = param;
      if (options.params[param] != null) {
        if (Array.isArray(options.params[param])) {
          options.params[param].forEach((arrayParam: string) => {
            params = params.append(paramName, arrayParam);
          });
        } else {
          params = params.append(paramName, options.params[param]);
        }
      }
    }
    const httpRequest = new HttpRequest(
        options.verb,
        options.url,
        JSON.stringify(options.data),
        {
          headers,
          params
        }
    );
    // start a timeout so we show please wait for anything that takes too long
    let timeoutId: any = null;
    if (options.pleaseWaitTimeoutDuration) {
      timeoutId = setTimeout(() => {
        this.pleaseWaitShowSource.next(options.pleaseWaitMessage);
      }, options.pleaseWaitTimeoutDuration);
    }
    this.showPleaseWait(options.pleaseWaitMessage, options.pleaseWaitContainerId);
    if (!this.config.webApiToken) {
      this.sessionActive = false;
      this.logoff(options);
    } else {
      // send the request
      return this.httpClient
          .request<ApiResult<T>>(httpRequest)
          .pipe(filter(event => event.type === HttpEventType.Response))
          .pipe(
              map((response: HttpResponse<ApiResult<T>>) => {
                const result = response.body;

                // indicate the request completed
                this.doneWaiting(timeoutId);
                this.hidePleaseWait(options.pleaseWaitContainerId);
                // if the result is not an ApiResult or if the result was successful
                // then just let the app handle the result
                if (result === undefined || result.status) {
                  // successful result, let the application handle it
                } else if (!result.message && result.modelErrors) {
                  // DSS Documentation on Validation - http://tiny.sc/cgvalidation
                  if (!options.shouldIgnoreInvalidResult) {
                    let msg = '';
                    for (const modelError of result.modelErrors) {
                      msg += modelError.errorMessage + '<br />';
                    }
                    this.showFailureMessage(msg, options.alertId);
                  }
                } else {
                  // DSS Documentation on handling exceptions - http://tiny.sc/cgclientexceptions
                  if (!options.shouldIgnoreFailureResult) {
                    this.showFailureMessage(result.message, options.alertId);
                  }
                }

                return result;
              }),
              catchError((err: HttpErrorResponse) => {
                // indicate the request completed
                this.doneWaiting(timeoutId);
                this.hidePleaseWait(options.pleaseWaitContainerId);

                return this.handleError<T>(err, options);
              })
          );
    }
  }

  private logoff(options: SocoHttpServiceOptions) {
    const url =
        '/' +
        (this.config.isSoCoEnv
            ? 'account/logoff'
            : this.constantsSvc.localIISAppName + '/account/logoff');
    // swal(this.constantsSvc.sessionswal, '', this.constantsSvc.warningSwal).then(
    //     () => {
    //       window.location.href = url;
    //     }
    // );
  }

  private getWebApiInfo(options: SocoHttpServiceOptions): Observable<string> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'x-transaction-id': this.getGuid(),
      'Cache-Control': 'no-cache',
      Pragma: 'no-cache'
    });

    // tslint:disable-next-line: prefer-immediate-return
    const req = this.httpClient.get<ApiResult<any>>('/Account/GetApiInfo', {headers})
        .pipe(
            map(result => {
              if (result.status) {
                // if the WebApiBaseUrl doesnt end with a forward slash, add it so we dont have to add it
                // everywhere in the code
                if (
                    result.data.webApiBaseUrl[
                    result.data.webApiBaseUrl.length - 1
                        ] !== '/'
                ) {
                  result.data.webApiBaseUrl += '/';
                }

                // add special configuration from the application including WebApiBaseUrl and WebApiToken
                // DSS Documentation on SocoConfigurationService - http://tiny.sc/cgsococonfigurationservice
                let webApiToken: string = null;
                for (const propName of Object.keys(result.data)) {
                  if (propName === 'webApiToken') {
                    webApiToken = result.data[propName];
                  }
                  this.config.setSetting(propName, result.data[propName]);
                }

                return webApiToken;
              } else {
                this.showFailureMessage(
                    'Unable to acquire a token',
                    options.alertId
                );
                return null;
              }
            }),
            catchError((err: HttpErrorResponse) => {
              const errMsg = 'Error occurred while acquiring a token';
              this.showFailureMessage(errMsg, options.alertId);

              return throwError(errMsg);
            })
        );

    return req;
  }

  private handleError<T>(
      error: HttpErrorResponse,
      options: SocoHttpServiceOptions
  ): Observable<ApiResult<T>> {
    let result = error.error as ApiResult<T>;
    let errMsg = '';

    // No ApiResult returned (e.g. 401 Unauthorized, 403 Forbidden)
    if (!result) {
      if (!options.shouldIgnoreErrorResult) {
        // server returned unsuccessful response code
        switch (error.status) {
          case 401:
            errMsg = 'Your token has expired';
            this.socoWebAuthService.redirectToLoginPage(); // redirect to login
            break;
          case 403:
            errMsg = 'Access denied';
            this.showFailureMessage(errMsg, options.alertId);
            break;
          default:
            errMsg = 'An error occurred';
            this.showFailureMessage(errMsg, options.alertId);
            break;
        }
      }

      result = {
        statusCode: error.status,
        status: false,
        message: errMsg,
        data: null,
        modelErrors: null
      };
      // ApiResult returned with modelErrors (e.g. 400 Bad Request)
    } else if (!result.message && result.modelErrors) {
      if (!options.shouldIgnoreInvalidResult) {
        for (const modelError of result.modelErrors) {
          errMsg += modelError.errorMessage + '<br />';
        }
        this.showFailureMessage(errMsg, options.alertId);
      }
      // An error ocurred and ApiResult was returned (e.g. 500 Internal Server Error)
    } else {
      if (!options.shouldIgnoreFailureResult) {
        this.showFailureMessage(result.message, options.alertId);
      }
    }

    return throwError(result);
  }

  private getGuid(): string {
    const hexlist = '0123456789abcdef';
    const b64list =
        'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/';

    function guidToBase64(
        guid: string,
        shouldUseLittleEndian: boolean
    ): string {
      let parsedGuid = guid.replace(/[^0-9a-f]/gi, '').toLowerCase();
      if (parsedGuid.length !== 32) {
        return '';
      }

      if (shouldUseLittleEndian) {
        parsedGuid =
            parsedGuid.slice(6, 8) +
            parsedGuid.slice(4, 6) +
            parsedGuid.slice(2, 4) +
            parsedGuid.slice(0, 2) +
            parsedGuid.slice(10, 12) +
            parsedGuid.slice(8, 10) +
            parsedGuid.slice(14, 16) +
            parsedGuid.slice(12, 14) +
            parsedGuid.slice(16);
      }
      parsedGuid += '0';

      let result = '';
      let i = 0;
      let a: any, p: any, q: any;
      while (i < 33) {
        a =
            (hexlist.indexOf(parsedGuid.charAt(i++)) << 8) |
            (hexlist.indexOf(parsedGuid.charAt(i++)) << 4) |
            hexlist.indexOf(parsedGuid.charAt(i++));

        p = a >> 6;
        q = a & 63;

        result += b64list.charAt(p) + b64list.charAt(q);
      }
      result += '==';

      return result;
    }

    // generate a guid
    const guid = v4();

    // base-64 encode the guid using Little-endian
    const b64encoded = guidToBase64(guid, true);

    // return the 22 character guid
    return b64encoded
        .replace('/', '_')
        .replace('+', '-')
        .substring(0, 22);
  }

  // DSS Documentation on handling exceptions - http://tiny.sc/cgclientexceptions
  private showFailureMessage(message: string, alertId: string) {
    if (!alertId) {
      // DSS Documentation on SocoAlertService - http://tiny.sc/cgsocoalertservice
      alertId = this.socoAlertService.getDefaultAlertId();
    }
    if ($('#' + alertId).length > 0) {
      // DSS Documentation on SocoAlertService - http://tiny.sc/cgsocoalertservice
      this.socoAlertService.error({message, id: alertId});
    } else {
      // simply log the failure to the console
      console.error(message);
    }
  }

  private doneWaiting(timeoutId: number) {
    // clear the timeout so please wait doesnt show if we finish in time
    clearTimeout(timeoutId);

    this.pleaseWaitHideSource.next();
  }

  public showPleaseWait(message: string, containerId: string) {
    // Added by John since please-wait is not a stand alone service
    this.pleaseWaitShowSource.next({message: message, containerId: containerId});
  }

  public hidePleaseWait(containerId: string) {
    // Added by John since please-wait is not a stand alone service
    this.pleaseWaitHideSource.next(containerId ? containerId : '');
  }
}

export enum SocoHttpServiceExceptionType {
  /**
   *  The result was successful and thus there is no exception.
   */
  None = 0,
  /**
   *  The result is not an ApiResult indicating a failure but rather some
   *  other type of exception.
   */
  Error = 1,
  /**
   *  Something happened server-side so an ApiResult has been returned to
   *  provide more information on what happened.
   */
  Failure = 2,
  /**
   *  The information in the model sent to the server had invalid data
   */
  Invalid = 3
}

export interface SocoHttpServiceOptions {
  success?: any;
  invliad?: any;
  pleaseWaitTitle?: any;
  /**
   *  The url of the API call being made
   */
  url: string;
  /**
   *  You do not need to specify this. When you use the appropriate function
   *  on SocoHttpService it will set the verb appropriately.
   */
  verb?: string;
  /**
   *  Data to be sent as the request message data.
   */
  data?: any;
  /**
   *  Map of strings or objects which will be serialized with the paramSerializer and appended as GET parameters.
   */
  params?: any;
  /**
   *  All of your API calls should communicate with your Web API project where your API
   *  controllers are. To avoid having to prefix all of your URLs with that URL SocoHttpService
   *  does it for you. However, if you are calling some other API you can specify that you
   *  don't want SocoHttpService to prefix the URL with the Web API URL.
   */
  excludeWebApiBaseUrl?: boolean;
  /**
   *  The id of the alert element to use if an error occurs and we want to display it to the user
   *
   *  DSS Documentation on handling exceptions - http://tiny.sc/cgclientexceptions
   */
  alertId?: string;
  /**
   *  Indicates if SocoHttpService should ignore a failure because you intend to add custom logic
   *  to handle the failure result.
   *
   *  DSS Documentation on handling exceptions - http://tiny.sc/cgclientexceptions
   */
  shouldIgnoreFailureResult?: boolean;
  /**
   *  Indicates if SocoHttpService should ignore an invalid result because you intend to add custom
   *  logic to handle the invalid result.
   *
   *  DSS Documentation on validation - http://tiny.sc/cgvalidation
   */
  shouldIgnoreInvalidResult?: boolean;
  /**
   *  Indicates if SocoHttpService should ignore an error because you intend to add custom logic
   *  to handle the error result.
   *
   *  DSS Documentation on handling exceptions - http://tiny.sc/cgclientexceptions
   */
  shouldIgnoreErrorResult?: boolean;
  /**
   *  How long should we wait before displaying a please wait - loading indicator.
   *  Specify 0 if we should never show a please wait - loading indicator.
   */
  pleaseWaitTimeoutDuration?: number;
  /**
   *  The message for the please wait - loading indicator.
   */
  pleaseWaitMessage?: string;

  // Added By John Hakeem
  // Please wait component use BlockUI JQuery plugin that need a container Id to function
  pleaseWaitContainerId?: string;
}
